/**
 * Pricing configuration — the ONE place every coefficient of the Quote Engine
 * lives (pricing/quote.ts) and of the post-run validation (pricing/validation.ts).
 *
 * v1 is a *conservative estimate*: with only ~10 real test PDFs available the
 * coefficients below are hand-set, never fitted. Nothing in the app learns
 * from the validation history; it only reports. Change a number here, bump
 * PRICING_VERSION, and the validation records stay comparable per version.
 *
 * All monetary amounts are TWD unless the name says Usd.
 */

/** Written into every QuoteSnapshot and PricingValidationRecord. */
export const PRICING_VERSION = 'v5';

/** Developer Mode notice lines (spec §5). */
export const PRICING_MODEL_LABEL = '目前估價模型：v1 保守估算';
export const PRICING_MODEL_NOTE = '樣本數不足，不進行自動模型擬合';

/**
 * Token estimation heuristic (no API call). Estimated provider usage =
 *
 *   translationInput  = billableChars × inputTokenFactor
 *                     + billableUnits × unitInputOverhead
 *                     + batches × promptTokensPerBatch
 *   translationOutput = billableChars × outputTokenFactor
 *                     + billableUnits × unitOutputOverhead
 *   batches           = max(ceil(units / maxBlocksPerBatch), ceil(chars / maxCharsPerBatch))
 *
 *   + terminologyReserve (fixed: the sample is capped at ~7 000 chars)
 *   + qaReserve          (share of the translation stage: ~5 % of the blocks,
 *                         source + translation + reviewer prompt)
 *
 * Reference point (README "Cost impact", 50-page paper, actual usage):
 * translation 35 939 in / 31 041 out, terminology 1 985 / 887, QA 5 451 / 2 088.
 *
 * v3 calibration (4 real runs, 35k–86k chars, 166–532 units): per-unit cost
 * dominates on fragmented documents (tables → many short units), per-char
 * output was overestimated. Fitted per-unit overhead ≈ 60–90 in / 30 out.
 */
export interface TokenEstimateConfig {
  inputTokenFactor: number;
  outputTokenFactor: number;
  unitInputOverhead: number;
  unitOutputOverhead: number;
  promptTokensPerBatch: number;
  maxBlocksPerBatch: number;
  maxCharsPerBatch: number;
  terminologyReserve: { inputTokens: number; outputTokens: number };
  qaReserve: { inputShare: number; outputShare: number };
}

export const PRICING_CONFIG: TokenEstimateConfig = {
  /** Input tokens per billable source character (v3: fitted on real runs). */
  inputTokenFactor: 0.17,
  /** Output tokens per billable source character (zh-TW translation + light reasoning, JSON quoting; v3: fitted). */
  outputTokenFactor: 0.25,
  /** Per-unit request overhead: JSON keys / id / quotes plus per-unit context (v3: fitted). */
  unitInputOverhead: 70,
  /** Per-unit reply overhead, reasoning included (v3: fitted). */
  unitOutputOverhead: 25,
  /** System prompt + glossary lines per request (batch.ts PROMPT_TOKENS_ESTIMATE). */
  promptTokensPerBatch: 650,
  /** Batch limits of translate/batch.ts (DEFAULT_MAX_BLOCKS / DEFAULT_MAX_CHARS). */
  maxBlocksPerBatch: 25,
  maxCharsPerBatch: 10_000,
  /** One /terminology request per run; the sample is capped, so a fixed reserve. */
  terminologyReserve: { inputTokens: 2_500, outputTokens: 1_000 },
  /** QA as a share of the translation stage (observed 0.15 in / 0.07 out). */
  qaReserve: { inputShare: 0.15, outputShare: 0.1 },
};

export interface ApiPricingConfig {
  inputPerMillionUsd: number;
  cachedInputPerMillionUsd: number;
  outputPerMillionUsd: number;
  usdToTwd: number;
}

/**
 * Provider list prices. Update here when the model or the exchange rate
 * changes; nothing else in the code base carries a price.
 *   model: gpt-5.6-terra (worker/wrangler.toml) — $2 / $12 per 1M tokens.
 */
export const API_PRICING_CONFIG: ApiPricingConfig = {
  inputPerMillionUsd: 2,
  cachedInputPerMillionUsd: 0.5,
  outputPerMillionUsd: 12,
  usdToTwd: 32.5,
};

/**
 * Quote protection only (never applied to actual costs): QA varies, output
 * length varies, fragmented units add overhead, retries happen, the exchange
 * rate moves. safeEstimatedApiCost = estimatedApiCostTwd × this.
 */
export const API_COST_SAFETY_FACTOR = 1.0;

/**
 * Payment fee reserve (PAYUNi not wired yet): max(price × rate, minimum).
 * Channels (PAYUNi): LINE Pay 3.17%, ATM transfer 1%, no fixed fee. One price
 * serves both, so the rate covers the dearer channel (LINE Pay).
 */
export const PAYMENT_PERCENT_RATE = 0.0317;
export const PAYMENT_MIN_FEE_TWD = 1;

/**
 * Target variable gross margin: (price − safeApiCost − paymentFee) / price ≥ this.
 * v3: the token estimate is calibrated on real runs (no systematic bias), so
 * the target applies directly: ≈40% actual margin.
 */
export const TARGET_MIN_MARGIN_RATE = 0.4;

/**
 * User-facing v1 tiers on billable characters (test prices, to be revised
 * with real data). `upTo` is inclusive; the last tier has no upper bound and
 * is extended by `extension` beyond `extension.from`.
 */
export interface PricingTier {
  /** Inclusive upper bound in billable chars; Infinity for the open tier. */
  upTo: number;
  priceTwd: number;
}

/**
 * v2: no tiers. The price is cost-based (minimumSustainablePrice, floored at
 * MIN_PRICE_TWD); the v1 tiers sat far above cost and pushed the actual margin
 * to ~70%.
 */
export const PRICING_TIERS: readonly PricingTier[] = [];

export interface TierExtension {
  charsPerStep: number;
  pricePerStep: number;
}

/** Above the last tier: last tier price + pricePerStep for every started charsPerStep. */
export const PRICING_TIER_EXTENSION: TierExtension = {
  charsPerStep: 40_000,
  pricePerStep: 15,
};

/** The suggested price is rounded UP to a multiple of this. */
export const PRICE_ROUND_STEP = 5;

/**
 * No valid translation is ever sold below this. v4: NT$5, so a single page or
 * chapter (API cost ≈ NT$1–4.5) is not priced like a whole paper; both payment
 * channels are percentage-based, so a small price still clears its fee.
 */
export const MIN_PRICE_TWD = 5;

/** A quote (and its quoteId) is valid for this long; PAYUNi will need it. */
export const QUOTE_TTL_MS = 30 * 60 * 1000;

/**
 * Post-run safety classification (Developer Mode only):
 *   LOSS     grossProfit < 0
 *   WARNING  0 ≤ grossMarginRate < warningBelowMargin
 *   SAFE     grossMarginRate ≥ warningBelowMargin
 */
export interface SafetyRules {
  warningBelowMargin: number;
}

export const PRICING_SAFETY_RULES: SafetyRules = {
  warningBelowMargin: 0.3,
};

/** Local validation dataset: newest N PricingValidationRecords in localStorage. */
export const PRICING_HISTORY_LIMIT = 50;
export const PRICING_HISTORY_STORAGE_KEY = 'pdfTranslator.pricingValidation';

/** Everything above, bundled, so a test can pass a modified copy. */
export interface PricingSettings {
  pricingVersion: string;
  tokens: TokenEstimateConfig;
  api: ApiPricingConfig;
  safetyFactor: number;
  paymentPercentRate: number;
  paymentMinFeeTwd: number;
  targetMinMarginRate: number;
  tiers: readonly PricingTier[];
  tierExtension: TierExtension;
  priceRoundStep: number;
  minPriceTwd: number;
  quoteTtlMs: number;
  safetyRules: SafetyRules;
}

export const DEFAULT_PRICING_SETTINGS: PricingSettings = {
  pricingVersion: PRICING_VERSION,
  tokens: PRICING_CONFIG,
  api: API_PRICING_CONFIG,
  safetyFactor: API_COST_SAFETY_FACTOR,
  paymentPercentRate: PAYMENT_PERCENT_RATE,
  paymentMinFeeTwd: PAYMENT_MIN_FEE_TWD,
  targetMinMarginRate: TARGET_MIN_MARGIN_RATE,
  tiers: PRICING_TIERS,
  tierExtension: PRICING_TIER_EXTENSION,
  priceRoundStep: PRICE_ROUND_STEP,
  minPriceTwd: MIN_PRICE_TWD,
  quoteTtlMs: QUOTE_TTL_MS,
  safetyRules: PRICING_SAFETY_RULES,
};
